import React, { useRef, useState, useEffect, Suspense } from "react";
import HeroZoomModel from "./HeroZoomModel";
import { Canvas } from "@react-three/fiber";
import watermark from "../images/home/watermark.png";

export default function Hero() {
    const [scrollY, setScrollY] = useState(0);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [heroRect, setHeroRect] = useState({ top: 0, height: window.innerHeight });
    const heroRef = useRef(null);

    // Track scroll
    useEffect(() => {
        const onScroll = () => setScrollY(window.scrollY || window.pageYOffset);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Track window resize
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Update hero section rect on mount and resize
    useEffect(() => {
        const updateRect = () => {
            if (heroRef.current) {
                const rect = heroRef.current.getBoundingClientRect();
                const scrollTop = window.scrollY || window.pageYOffset;
                setHeroRect({ top: rect.top + scrollTop, height: rect.height });
            }
        };
        updateRect();
        window.addEventListener("resize", updateRect);
        return () => window.removeEventListener("resize", updateRect);
    }, []);

    // Calculate scroll progress relative to hero
    const scrollProgress = Math.min(Math.max((scrollY - heroRect.top) / heroRect.height, 0), 1);

    // Fade overlay from 50% → 70% scroll
    const overlayOpacity = Math.min(Math.max((scrollProgress - 0.5) / 0.2, 0), 1);

    return (
        <div
            ref={heroRef}
            className="relative bg-gray-100 w-full flex flex-col md:flex-row items-center justify-center overflow-hidden select-none min-h-screen"
        >
            {/* Text Section */}
            <div className="relative z-30 flex flex-col md:flex-row items-center justify-between w-full mx-auto px-6 md:px-20 pt-24 md:pt-0 pb-16 md:pb-0">
                <div className="flex-1 md:basis-1/3 flex flex-col items-start justify-center text-center md:text-left md:pr-8">
                    <h2 className="text-xl md:text-2xl font-semibold mb-4 text-black w-full">
                        The Future of Weight Management
                    </h2>
                    <p className="text-base md:text-xl text-left text-gray-800 mb-0 leading-relaxed md:w-full">
                        Mounjaro (tirzepatide) is the first and only dual GIP/GLP-1 receptor agonist.<br/> It represents a new class of medicine that helps you lose weight and keep it off<br/> by working with your body's natural hunger signals.
                    </p>
                </div>
            </div>

            {/* 3D Model Canvas */}
            <div className="w-full z-20 md:absolute md:inset-0 mt-8 md:mt-0">
                <Suspense fallback={<div className="text-center">Loading 3D...</div>}>
                    <Canvas
                        dpr={[1, 2]}
                        gl={{ antialias: true, alpha: true, powerPreference: "low-power" }}
                        camera={{ position: [0, 0, 3.8], fov: 35 }}
                    >
                        <ambientLight intensity={0.8} />
                        <directionalLight position={[2, 2, 5]} intensity={0.7} />
                        <HeroZoomModel scrollY={scrollY} isMobile={windowWidth < 768} />
                    </Canvas>
                </Suspense>
            </div>

            {/* Overlay for all screens */}
            <div
                className="absolute inset-0 z-40 pointer-events-none bg-[#B9B9B9]"
                style={{ opacity: overlayOpacity, transition: "opacity 0.3s linear" }}
            />

            {/* Watermark */}
            <div className="absolute left-0 bottom-0 w-full text-center select-none pointer-events-none z-10 px-6 md:px-20 mb-5">
                <img
                    src={watermark}
                    alt="Mounjaro Watermark"
                    style={{
                        width: "100%",
                        height: "auto",
                        opacity: 0.4,
                        objectFit: "contain",
                        pointerEvents: "none",
                        userSelect: "none",
                    }}
                />
            </div>
        </div>
    );
}
